import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import UniversalHeader from '@/components/shared/UniversalHeader';
import UniversalFooter from '@/components/shared/UniversalFooter';
import { CheckCircle2, ShieldCheck, Users, LineChart, Stethoscope, ArrowRight, PlayCircle } from 'lucide-react';

export default function ProvidersPage() {
    const benefits = [
        {
            title: "Emergency Access to Records",
            description: "When a new patient walks in during an emergency, get instant access to their allergies, medications, and medical history—if they've shared it with you.",
            icon: Users,
            color: "text-blue-600",
            bg: "bg-blue-50"
        },
        {
            title: "Reduce Waiting Times",
            description: "Patients log symptoms, vitals, and photos at home before their visit. Review their notes ahead of time and spend more time on diagnosis, less on intake.",
            icon: Stethoscope,
            color: "text-emerald-600",
            bg: "bg-emerald-50"
        },
        {
            title: "Patient-Controlled Sharing",
            description: "No more chasing records from other facilities. Patients share what you need, when you need it. Access expires automatically when treatment ends.",
            icon: ShieldCheck,
            color: "text-purple-600",
            bg: "bg-purple-50"
        },
        {
            title: "Complete Medical Context",
            description: "See lab results from other providers, medication adherence tracked at home, and visit notes from specialists—all in one timeline.",
            icon: LineChart,
            color: "text-rose-600",
            bg: "bg-rose-50"
        }
    ];

    return (
        <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-[#D64463] selection:text-white overflow-x-hidden">
            <UniversalHeader className="bg-[#0F172A] border-transparent text-white" />

            {/* Hero Section - Clean, High Contrast, Professional */}
            <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-slate-50">

                {/* Subtle Geometric Pattern Background - Replaces muddy blobs */}
                <div className="absolute inset-0 opacity-[0.03]"
                    style={{ backgroundImage: 'radial-gradient(#94a3b8 1px, transparent 1px)', backgroundSize: '32px 32px' }}>
                </div>

                {/* Right Side Accent Blob - Kept clean and distant from text */}
                <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-b from-blue-100/50 to-purple-100/50 rounded-full blur-3xl opacity-60 translate-x-1/2 -translate-y-1/2 pointer-events-none" />

                <div className="container mx-auto px-4 relative z-10">
                    <div className="flex flex-col lg:flex-row items-center gap-16">
                        {/* Text Content */}
                        <div className="flex-1 text-left space-y-8 animate-fade-in-up">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm">
                                <span className="flex h-2 w-2 rounded-full bg-[#D64463]"></span>
                                <span className="text-sm font-bold text-slate-700 tracking-wide uppercase">For Healthcare Providers</span>
                            </div>

                            <h1 className="text-5xl lg:text-7xl font-extrabold tracking-tight leading-[1.1] text-slate-900">
                                Stop chasing <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D64463] to-[#9C27B0]">
                                    patient records
                                </span>
                            </h1>

                            <p className="text-xl text-slate-600 leading-relaxed max-w-xl">
                                When your patients use CareVault, they can share their complete medical history with you instantly. No more waiting days for faxed records or piecing together incomplete information from multiple sources.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4 pt-2">
                                <Button asChild size="lg" className="h-14 px-8 rounded-full bg-slate-900 text-white hover:bg-slate-800 text-lg font-bold shadow-lg shadow-slate-900/20 transition-all hover:scale-105">
                                    <Link to="/register">
                                        Register Your Practice <ArrowRight className="ml-2 h-5 w-5" />
                                    </Link>
                                </Button>
                                <Button asChild variant="outline" size="lg" className="h-14 px-8 rounded-full border-2 border-slate-200 hover:bg-white hover:border-slate-300 text-lg font-bold text-slate-700">
                                    <Link to="/contact">
                                        <PlayCircle className="mr-2 h-5 w-5 text-slate-400" /> See How It Works
                                    </Link>
                                </Button>
                            </div>
                        </div>

                        {/* Hero Image / Visual - High Quality */}
                        <div className="flex-1 relative animate-fade-in-up animation-delay-200 hidden lg:block">
                            <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl shadow-slate-200 border border-slate-100 bg-white">
                                <img
                                    src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                                    alt="Modern Clinic Dashboard"
                                    className="w-full h-auto"
                                />
                                {/* Floating Card Overlay */}
                                <div className="absolute bottom-8 left-8 bg-white/95 backdrop-blur-md p-4 rounded-xl shadow-lg border border-slate-100 flex items-center gap-4 max-w-xs animate-slide-in-right">
                                    <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                        <CheckCircle2 className="h-6 w-6 text-green-600" />
                                    </div>
                                    <div>
                                        <div className="font-bold text-slate-900">Sync Complete</div>
                                        <div className="text-sm text-slate-500">Patient records updated instantly</div>
                                    </div>
                                </div>
                            </div>

                            {/* Decorative Elements behind image */}
                            <div className="absolute -z-10 top-10 right-10 w-full h-full bg-gradient-to-br from-[#D64463]/10 to-[#9C27B0]/10 rounded-2xl -rotate-6 scale-105"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Grid */}
            <section className="py-24 bg-white relative">
                <div className="container mx-auto px-4">
                    <div className="text-center max-w-3xl mx-auto mb-20">
                        <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">How it works for providers</h2>
                        <p className="text-xl text-slate-500">Your patients share their records with you. You get instant access. It's that simple.</p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {benefits.map((benefit, idx) => (
                            <div key={idx} className="group p-8 rounded-3xl bg-slate-50 hover:bg-white border border-transparent hover:border-slate-100 hover:shadow-xl transition-all duration-300">
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${benefit.bg} group-hover:scale-110 transition-transform duration-300`}>
                                    <benefit.icon className={`h-7 w-7 ${benefit.color}`} />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                                <p className="text-slate-600 leading-relaxed">
                                    {benefit.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Premium Call to Action */}
            <section className="py-32 relative overflow-hidden">
                <div className="absolute inset-0 bg-[#0F172A]"></div>
                {/* Abstract shapes */}
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3"></div>
                <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[#D64463]/20 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3"></div>

                <div className="container mx-auto px-4 relative z-10 text-center">
                    <h2 className="text-4xl md:text-6xl font-bold text-white mb-8 tracking-tight">
                        Ready to see your patients' full story?
                    </h2>
                    <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-12 font-medium">
                        Register your practice today and start receiving patient-shared health records instantly.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-6 justify-center">
                        <Button size="lg" asChild className="h-16 px-12 rounded-full bg-white text-slate-900 hover:bg-slate-50 font-bold text-xl hover:scale-105 transition-transform shadow-2xl">
                            <Link to="/register">Register Your Practice</Link>
                        </Button>
                    </div>
                </div>
            </section>

            <UniversalFooter />
        </div>
    );
}
